export const instructionData = [
  {
    title: 'Helpful Resources',
    document: `
      <div class="instruction-content">
        <h2>Helpful Resources</h2>
        <p><a href="https://www.hvacrschool.com/calculators/" class="external-link" target="_blank" rel="noopener noreferrer">HVAC School Calculators</a></p>
        <p><a href="https://www.youtube.com/channel/UCY2TvazSji5Jx8qxbbvHyXA" class="external-link" target="_blank" rel="noopener noreferrer">Unity Build YouTube Channel</a></p>
      </div>
      `,
  },
  {
    title: 'Projects',
    document: `
      <div class="instruction-content">
        <h2>Projects</h2>
        <p>Learn about projects in Unity Build.</p>
        
        <h3>Create a Project</h3>
        <ul>
          <li>Click the [+ Create Project] button adjacent to search bar</li>
          <li>Fill in the project details:
            <ul>
              <li>Project name</li>
              <li>Project Address</li>
              <li>Project Description</li>
              <li>Construction Start and End Dates</li>
            </ul>
          </li>
          <li>Click the [Create Project] button to save</li>
        </ul>
        <p>Selecting a project will redirect you to the "Takeoff List" where you can then select your project and proceed to create an estimate.</p>
      </div>
    `,
  },
  {
    title: 'Estimates',
    document: `
      <div class="instruction-content">
        <h2>Estimates</h2>
        <p>Learn how to create and manage estimates in Unity Build. Think of estimates as your competitive bids - they represent your proposals for projects you're trying to win.</p>

        <h3>Creating a New Estimate</h3>
        <ul>
          <li>Click the [+ Create Estimate] button adjacent to search bar</li>
          <li>Fill in the estimate details:
            <ul>
              <li>Estimate name</li>
              <li>Master Format Division</li>
              <li>Estimate Description</li>
              <li>Estimate Id</li>
              <li>Estimate Due Date</li>
            </ul>
          </li>
          <li>Click the [Create Estimate] button to save</li>
        </ul>
        <p>Selecting an estimate will redirect you to the "Master Project View" where you can then add documents to the project and create a takeoff.</p>

        <h3>Understanding Estimate Statuses</h3>
        <p>Statuses are your communication tool - they help you and your team track where each bid stands in your sales process. Think of them as progress markers that everyone can see at a glance.</p>
        
        <h4>Available Statuses and When to Use Them:</h4>
        <ul>
          <li><strong>Draft:</strong> Your estimate is still being developed. Use this while you're building quantities, pricing, and finalizing your bid strategy.</li>
          <li><strong>Pending:</strong> Your estimate is complete and waiting for internal review, pricing approval, or submission decision. Great for tracking bids in your approval process.</li>
          <li><strong>Rejected:</strong> The client has declined your bid. Use this to track projects where you submitted but weren't selected, helping you analyze win rates.</li>
          <li><strong>Declined:</strong> Your team decided not to pursue or submit this bid. Maybe the project wasn't a good fit, timing was wrong, or you chose not to compete.</li>
          <li><strong>Void:</strong> The estimate is no longer valid - perhaps the project was cancelled, scope changed dramatically, or you need to start over with a new estimate.</li>
          <li><strong>Accepted:</strong> Congratulations! You won the bid. This status unlocks contract creation and locks your estimate to preserve the winning numbers.</li>
        </ul>
        
        <h4>Why Use Statuses?</h4>
        <ul>
          <li><strong>Team Communication:</strong> Everyone knows instantly where each bid stands in your sales pipeline</li>
          <li><strong>Workflow Organization:</strong> Filter and sort your estimates by status to focus on what needs attention</li>
          <li><strong>Performance Analysis:</strong> Track win rates and identify patterns in your bidding success</li>
          <li><strong>System Integration:</strong> Unity Build uses specific statuses to unlock important features (especially "Accepted")</li>
        </ul>

        <h3>The "Accepted" Status - Your Gateway to Contracts</h3>
        <p>The "Accepted" status is special - it's like crossing the finish line in your bidding process. Here's what happens when you mark an estimate as "Accepted":</p>
        
        <h4>What "Accepted" Does For You:</h4>
        <ul>
          <li><strong>Contract Ready:</strong> Only "Accepted" estimates can be converted into contracts</li>
          <li><strong>Budget Foundation:</strong> The estimate becomes your contract's initial budget - a clean starting point</li>
          <li><strong>Cost Protection:</strong> Unity Build locks the estimate to preserve your accepted pricing</li>
          <li><strong>Change Order Setup:</strong> Now you can add change orders to modify the original scope while maintaining a clear audit trail</li>
        </ul>

        <h4>The Locking Mechanism - Protecting Your Budget</h4>
        <p>Once an estimate reaches "Accepted" status, Unity Build automatically locks any actions that could affect its costs. Think of this as freezing your bid at the moment of acceptance. This means:</p>
        <ul>
          <li>No one can accidentally modify the takeoff quantities</li>
          <li>Unit prices and labor rates are preserved exactly as bid</li>
          <li>Your accepted budget becomes the reliable baseline for the entire project</li>
        </ul>

        <h3>The Estimate-to-Contract Workflow</h3>
        <p>Here's how the process flows from bid to project execution:</p>
        <ol>
          <li><strong>Create Estimate:</strong> Build your competitive bid</li>
          <li><strong>Update Status:</strong> Track progress through your sales process</li>
          <li><strong>Mark "Accepted":</strong> Lock in your winning bid</li>
          <li><strong>Convert to Contract:</strong> Transform the estimate into a project contract</li>
          <li><strong>Manage Changes:</strong> Use change orders to modify scope while preserving the original budget and audit trail</li>
        </ol>

        <h3>Best Practices for Status Management</h3>
        <ul>
          <li><strong>Keep Statuses Current:</strong> Update them as your bid progresses through your sales process - your team relies on accurate status information</li>
          <li><strong>Use Draft Liberally:</strong> Keep estimates in "Draft" while you're still making changes - only move to "Pending" when you're ready for review</li>
          <li><strong>Track Everything:</strong> Don't forget to update "Rejected" and "Declined" estimates - this data helps you analyze your bidding performance</li>
          <li><strong>Be Decisive with Accepted:</strong> Only mark estimates as "Accepted" when you've truly won the project - this status has permanent consequences</li>
        </ul>

        <h3>Pro Tips for Students</h3>
        <ul>
          <li><strong>Status as Communication:</strong> Think of status updates as sending a message to your entire team about where this opportunity stands</li>
          <li><strong>Business Intelligence:</strong> Well-maintained statuses help you track win rates, identify successful strategies, and improve future bids</li>
          <li><strong>Only Accept When Ready:</strong> Remember that "Accepted" locks your estimate, so make sure your numbers are final</li>
          <li><strong>Think Long-Term:</strong> The estimate you create today becomes the foundation for potentially years of project management</li>
          <li><strong>Audit Trail Advantage:</strong> By using the estimate-to-contract conversion, you maintain a clear history of how costs evolved from initial bid to final project completion</li>
        </ul>
      </div>
    `,
  },
  {
    title: 'Contracts & Change Orders',
    document: `
      <div class="instruction-content">
        <h2>Contract Management System</h2>
        <p>Unity Build's Contract Management System transforms your accepted estimates into active project contracts and provides sophisticated tools for managing scope changes throughout project execution.</p>

        <h3>Understanding the Estimate-to-Contract Relationship</h3>
        <p>The foundation of Unity Build's contract system is built on a clear hierarchy:</p>
        <ul>
          <li><strong>Estimates → Contracts:</strong> Only estimates with "Accepted" status can be assigned to contracts</li>
          <li><strong>One-to-One Assignment:</strong> Each estimate can only be assigned to one contract</li>
          <li><strong>One-to-Many Capacity:</strong> A single contract can contain multiple estimates</li>
          <li><strong>Budget Foundation:</strong> Assigned estimates establish the initial budget structure for the contract</li>
        </ul>

        <h3>Contracts: Your Project Management Hub</h3>
        <p>Contracts serve as the central management tool for active projects, providing:</p>
        
        <h4>Multi-Scope Project Management</h4>
        <ul>
          <li><strong>Scope Organization:</strong> Each estimate assigned to a contract represents a distinct scope of work</li>
          <li><strong>Budget Composition:</strong> The contract's total budget comprises all assigned estimate budgets</li>
          <li><strong>Clear Responsibility:</strong> Different scopes can be managed independently while maintaining overall project visibility</li>
        </ul>

        <h4>Example: Multi-Scope Contract</h4>
        <p>A building renovation contract might include:</p>
        <ul>
          <li>Estimate A: "Electrical Systems Upgrade" - $150,000</li>
          <li>Estimate B: "HVAC Installation" - $200,000</li>
          <li>Estimate C: "Fire Safety Systems" - $75,000</li>
          <li><strong>Total Contract Budget: $425,000</strong></li>
        </ul>

        <h3>Change Orders: Granular Scope Management</h3>
        <p>Change orders provide precise control over project modifications and budget adjustments:</p>

        <h4>Scope-Specific Assignment</h4>
        <ul>
          <li><strong>Targeted Assignment:</strong> Each change order is assigned to a specific scope (estimate) within the contract</li>
          <li><strong>Clear Impact Tracking:</strong> You can see exactly which scope is affected by each change</li>
          <li><strong>Organized Management:</strong> Changes are grouped by scope, making it easy to understand cost progression</li>
          <li><strong>Audit Trail:</strong> Maintain complete visibility of how each scope has evolved from original estimate</li>
        </ul>

        <h4>Budget Impact Rules</h4>
        <p>Change orders affect contract budgets based on their status:</p>
        <ul>
          <li><strong>Approved Change Orders:</strong> Automatically added to the contract budget</li>
          <li><strong>All Other Statuses:</strong> Do not affect the contract budget until approved</li>
          <li><strong>Budget Protection:</strong> Only confirmed changes impact your project financials</li>
          <li><strong>Status Control:</strong> Use change order statuses to manage approval workflows</li>
        </ul>

        <h3>The Complete Workflow: From Bid to Project Completion</h3>
        <ol>
          <li><strong>Create Estimates:</strong> Build competitive bids for different scopes of work</li>
          <li><strong>Mark as Accepted:</strong> When you win the work, update estimate status to "Accepted"</li>
          <li><strong>Create Contract:</strong> Establish the project contract and assign accepted estimates</li>
          <li><strong>Establish Budgets:</strong> Contract budget automatically reflects all assigned estimate values</li>
          <li><strong>Manage Changes:</strong> Create change orders assigned to specific scopes as needed</li>
          <li><strong>Control Budget Impact:</strong> Approve change orders to add them to contract budget</li>
          <li><strong>Track Performance:</strong> Monitor actual costs against budgets for each scope</li>
        </ol>

        <h3>Strategic Benefits of This System</h3>
        
        <h4>For Project Managers:</h4>
        <ul>
          <li><strong>Clear Scope Boundaries:</strong> Each estimate represents a distinct area of responsibility</li>
          <li><strong>Change Impact Visibility:</strong> See exactly how modifications affect specific scopes</li>
          <li><strong>Budget Control:</strong> Only approved changes affect project financials</li>
          <li><strong>Performance Tracking:</strong> Compare actual vs. budgeted costs by scope</li>
        </ul>

        <h4>For Financial Management:</h4>
        <ul>
          <li><strong>Protected Budgets:</strong> Original accepted estimates remain intact as baseline</li>
          <li><strong>Controlled Changes:</strong> Budget modifications require explicit approval</li>
          <li><strong>Audit Trail:</strong> Complete history of how project costs evolved</li>
          <li><strong>Scope Accountability:</strong> Track profitability and performance by individual scope</li>
        </ul>

        <h4>For Client Relations:</h4>
        <ul>
          <li><strong>Transparent Changes:</strong> Clear documentation of what changed and why</li>
          <li><strong>Scope-Specific Billing:</strong> Organize invoicing by distinct work areas</li>
          <li><strong>Change Justification:</strong> Link each change order to its specific scope impact</li>
          <li><strong>Professional Documentation:</strong> Maintain clear records for client discussions</li>
        </ul>

        <h3>Best Practices for Contract Management</h3>
        
        <h4>Setting Up Contracts:</h4>
        <ul>
          <li><strong>Logical Scope Division:</strong> Organize estimates by natural work boundaries (electrical, mechanical, etc.)</li>
          <li><strong>Clear Responsibility:</strong> Ensure each scope has obvious ownership and accountability</li>
          <li><strong>Appropriate Scale:</strong> Don't create too many small scopes - aim for meaningful work packages</li>
        </ul>

        <h4>Managing Change Orders:</h4>
        <ul>
          <li><strong>Accurate Assignment:</strong> Always assign change orders to the most appropriate scope</li>
          <li><strong>Status Discipline:</strong> Use change order statuses consistently across your organization</li>
          <li><strong>Documentation:</strong> Provide clear descriptions of what's changing and why</li>
          <li><strong>Approval Process:</strong> Establish clear workflows for change order approval</li>
        </ul>

        <h4>Monitoring Performance:</h4>
        <ul>
          <li><strong>Regular Review:</strong> Monitor actual costs vs. budgets for each scope</li>
          <li><strong>Trend Analysis:</strong> Identify patterns in change orders and scope performance</li>
          <li><strong>Early Warning:</strong> Flag scopes that are trending over budget</li>
          <li><strong>Lessons Learned:</strong> Use performance data to improve future estimating</li>
        </ul>

        <h3>Common Scenarios and Solutions</h3>
        
        <h4>Scenario: Multi-Trade Project</h4>
        <p><strong>Challenge:</strong> Managing a project with electrical, plumbing, and HVAC work</p>
        <p><strong>Solution:</strong> Create separate estimates for each trade, assign all to one contract. Change orders are assigned to the specific trade scope affected.</p>

        <h4>Scenario: Phased Construction</h4>
        <p><strong>Challenge:</strong> Project has multiple phases with different timing</p>
        <p><strong>Solution:</strong> Create estimates for each phase, assign to contract. Track change orders by phase to understand scope evolution.</p>

        <h4>Scenario: Design Changes</h4>
        <p><strong>Challenge:</strong> Client requests design modifications affecting multiple scopes</p>
        <p><strong>Solution:</strong> Create separate change orders for each affected scope, clearly linking the change to its specific impact area.</p>

        <h3>Integration with Other Unity Build Features</h3>
        <ul>
          <li><strong>Takeoff Integration:</strong> Contract scopes maintain connection to original takeoff quantities</li>
          <li><strong>Reporting:</strong> Generate reports by contract, estimate, or change order</li>
          <li><strong>Performance Analytics:</strong> Track estimating accuracy and project profitability by scope (coming soon)</li>
        </ul>

        <p>The Contract Management System transforms Unity Build from an estimating tool into a complete project management platform, providing the structure and controls needed for successful project delivery.</p>
      </div>
    `,
  },
  {
    title: 'Master Project View',
    document: `
      <div class="instruction-content">
        <h2>Master Project View</h2>
        <p>
          The Master Project View is the central workspace where you perform and organize your takeoff.
        </p>
        
        <h3>Main Menu</h3>
        <p>
          Located at the top of the screen, the main menu provides navigation to:
          <ul>
            <li>Item and Assembly Manager - create and manage your takeoff components</li>
            <li>Project/Takeoff Lists - return to your project dashboard</li>
            <li>Closeout Tab - finalize your estimations</li>
            <li>Current Project Information - view details about your active estimate</li>
          </ul>
        </p>
        
        <h3>Toolbar</h3>
        <p>
          The toolbar contains essential takeoff tools:
          <ul>
            <li>Documents Interface - add plans and specifications for takeoff and AI context</li>
            <li>Scale Setting - establish measurement scale for accurate quantification</li>
            <li>Temporary Measurements - take quick measurements without creating permanent items
              <ul>
                <li><em>Controls:</em> Press M to toggle measurement mode, draw on screen, then press M again to free your cursor. Use the "Clear all measurements" button in toolbar to clear.</li>
              </ul>
            </li>
            <li>Cross Hair - precise positioning tool</li>
            <li>Polar Mode - measure using angle and distance</li>
            <li>Refresh Button - optimize for multi-monitor setups</li>
            <li>Visibility Control - toggle takeoff palette visibility</li>
          </ul>
        </p>
        
        <h3>Document Viewport</h3>
        <p>
          The central area displays the current page of your selected document where you perform your takeoff measurements and counts.
        </p>
        
        <h3>Takeoff Palette</h3>
        <p>
          Located on the right side, this panel allows you to:
          <ul>
            <li>Select assemblies for takeoff</li>
            <li>Create breakouts to organize counts for schedule of values</li>
            <li>View and manage your takeoff items hierarchically</li>
          </ul>
        </p>
        
        <h3>Chat Interface</h3>
        <p>
          In the lower left corner, the toggleable chat interface enables you to:
          <ul>
            <li>Interact with your documents using AI assistance</li>
            <li>Request internet searches for additional information</li>
            <li>Get contextual help based on your current project</li>
          </ul>
        </p>
        
        <p>
          To begin your takeoff, add documents via the toolbar, select an assembly from the takeoff palette "assemblies" or Audit Trail, and click the document with an takeoff assembly enabled.
        </p>
      </div>
    `,
  },
  {
    title: 'Documents',
    document: `
      <div class="instruction-content">
        <h2>Documents</h2>
        <p>The Documents interface allows you to manage all files associated with your project.</p>

        <h3>Document Management</h3>
        <p>This interface provides simple tools to organize your project documents:</p>
        <ul>
          <li><strong>Upload:</strong> Add new documents to your project (PDF only as of 4/14/25)</li>
          <li><strong>Rename:</strong> Change document names for better organization</li>
          <li><strong>Delete:</strong> Remove documents that are no longer needed</li>
        </ul>
        <p><strong>Note:</strong> Document processing may take up to 20 seconds after upload.</p>

        <h3>AI Context Integration</h3>
        <p>All uploaded documents are automatically added to your AI assistant's context, enabling it to:</p>
        <ul>
          <li>Answer questions specific to your project documents</li>
          <li>Reference plans, specifications, and other uploaded materials</li>
          <li>Provide insights based on document content</li>
        </ul>

        <h3>Accessing Documents</h3>
        <p>Once uploaded, documents will be available in the document viewport where you can:</p>
        <ul>
          <li>View and navigate through document pages</li>
          <li>Perform takeoff measurements on plans</li>
          <li>Reference specifications during estimating</li>
        </ul>
      </div>
    `,
  },
  {
    title: 'Item & Assembly Manager',
    document: `
      <div class="instruction-content">
        <h2>Item & Assembly Manager</h2>
        <p><a href="https://youtu.be/AoN4NJVlXmQ?si=muSBKRuCD4pvpVm-" class="external-link" target="_blank" rel="noopener noreferrer">YouTube Video: Item & Assembly Manager</a></p>
        <p>The Item and Assembly Manager is the all-in-one interface where items and assemblies are created and managed.</p>
        
        <h3>Manager Modes</h3>
        <p>The manager has two distinct modes:</p>
        <ul>
          <li><strong>Assembly Mode:</strong> For creating and managing assemblies</li>
          <li><strong>Item Mode:</strong> For creating and managing individual items</li>
        </ul>
        <p>You can toggle between these modes using either:</p>
        <ul>
          <li>The button labeled "Enter Item Mode" or "Enter Assembly Mode"</li>
          <li>The wrench icon on the item or assembly folder</li>
        </ul>
        <p>More detailed information about building items or assemblies is available in their respective instruction modules.</p>
        
        <h3>Items</h3>
        <p>In this interface, you can:</p>
        <ul>
          <li>Create items for your takeoffs</li>
          <li>Assign production or unit prices to items</li>
          <li>Modify item properties at any time</li>
        </ul>
        
        <h3>Assemblies</h3>
        <p>Assemblies are containers that hold one or more items. They serve as the core engine of how estimates are quantified:</p>
        <ul>
          <li>Create assemblies to organize related items</li>
          <li>Add multiple items to a single assembly</li>
          <li>Customize how items are quantified through a formulaic interface</li>
          <li>Configure how items are counted as they are drawn on the screen</li>
        </ul>
        
        <p>The assembly system allows you to create sophisticated takeoff strategies while maintaining organization and consistency across your estimates.</p>
      </div>
    `,
  },
  {
    title: 'User Created Items',
    document: `
      <div class="instruction-content">
        <h2>Items</h2>
        <p>Items are organized using a powerful and flexible system designed to handle the wide variety of construction materials efficiently.</p>
        
        <h3>Item Organization</h3>
        <p>Each item is defined by two key elements:</p>
        <ul>
          <li><strong>Primary Attribute:</strong> The characteristic most responsible for cost differences (typically size or dimension)</li>
          <li><strong>Item Name:</strong> The standard name of the material or component</li>
        </ul>
        
        <h3>Creating an Item</h3>
        <p>Once you have created attribute groups, follow these steps to create an item:</p>
        <ol>
          <li>Select the folder on the left where you want to add the item</li>
          <li>Enter the item name in the Item Details section and choose the desired options from dropdown menus</li>
          <li>Select the attributes you want for this item via the checkboxes</li>
          <li>Click the Save button</li>
        </ol>
        <p>After creating the item:</p>
        <ol>
          <li>Select the edit icon for the item in the item folder</li>
          <li>Set the unit prices and production values as needed</li>
        </ol>
        
        <h3>Attribute Groups</h3>
        <p>The system uses attribute groups to streamline item creation:</p>
        <ul>
          <li>Create a single attribute group containing all available sizes/variations for a material type</li>
          <li>Assign the entire attribute group to an item name</li>
          <li>Reuse attribute groups across similar items</li>
        </ul>
        
        <h3>Example</h3>
        <p>For conduit materials:</p>
        <ol>
          <li>Create an attribute group with sizes ½" through 4"</li>
          <li>Assign this group to "Electrical Metallic Tubing (EMT)"</li>
          <li>Reuse the same attribute group for "Schedule 40 PVC Conduit"</li>
        </ol>
        
        <p>This approach allows for efficient creation of multiple related items while maintaining logical organization.</p>
      </div>
    `,
  },
  {
    title: '🌍 Community Items',
    document: `
      <div class="instruction-content">
        <h2>🌍 Community Items</h2>
        
        <h3>What Are Community Items?</h3>
        <p>Community Items are items that have been curated by Unity Build and are available for all users of the Unity Build estimation system. These items represent a collaborative approach to building a comprehensive database of construction materials and their associated costs and production values.</p>
        
        <h3>Current Pricing Model</h3>
        <p>Currently, Unity Build sources pricing data directly from vendors and suppliers, which typically favors contractor pricing over over-the-counter retail pricing. This approach provides more accurate cost estimates for professional construction projects, though pricing can fluctuate based on market conditions, regional variations, and supplier relationships.</p>
        
        <h3>User Responsibility and Regional Variations</h3>
        <p><strong>Important:</strong> Community Items provide reference values based on vendor data and should be considered starting points for your estimates. We expect users to actively review and override costs and production values on the closeout screen with values they believe are correct for their specific project and region.</p>
        
        <p>Costs and production values can vary significantly by region, market conditions, and project specifics. The values provided should be considered reference estimates based on vendor relationships, not guaranteed purchase prices. Unity Build makes no warranties regarding the accuracy, completeness, or suitability of these values for any particular project.</p>
        
        <h3>Using Community Items in Assemblies</h3>
        <p>Using Community Items in assemblies is much like using user-created items, with one key difference: they are managed by Unity Build and sourced from vendor relationships rather than requiring you to create and manage the costs and production values yourself. However, you remain responsible for verifying and adjusting these values based on your project requirements and local market conditions.</p>
        
        <h3>Benefits of Community Items</h3>
        <ul>
          <li><strong>Time Savings:</strong> No need to research and input costs for common materials</li>
          <li><strong>Vendor-Sourced Data:</strong> Provides contractor-focused pricing from direct supplier relationships</li>
          <li><strong>Professional Focus:</strong> Pricing optimized for construction professionals rather than retail customers</li>
          <li><strong>Flexibility:</strong> Easy to override with project-specific values</li>
          <li><strong>Future Enhancement:</strong> Will benefit from community feedback integration as the system develops</li>
        </ul>
        
        <p>Community Items serve as professional reference tools based on vendor relationships, with plans to enhance them through community insights. Professional judgment and local market knowledge should always guide final pricing decisions.</p>
      </div>
    `,
  },
  {
    title: 'Item Attributes',
    document: `
      <div class="instruction-content">
        <h2>Item Attributes</h2>
        
        <h3>Prerequisite: Attribute Setup</h3>
        <p>Before creating items, you must first create attribute groups and attributes:</p>
        <ol>
          <li>Create attribute groups that represent categories of variation (sizes, materials, etc.)</li>
          <li>Add individual attributes to each group</li>
        </ol>
        
        <h3>Tips for Creating Attributes</h3>
        <ul>
          <li><strong>Attributes vs. Item Names:</strong> 
            <ul>
              <li>Attributes should be specific characteristics that vary within a product type (½", ¾", 1", etc.)</li>
              <li>Item names should be the general product category (EMT, PVC Conduit, etc.)</li>
            </ul>
          </li>
          <li>Group attributes logically by the type of variation they represent</li>
          <li>Consider which attributes will most impact pricing when organizing your structure</li>
        </ul>
        
        <h3>Attribute Variables</h3>
        <p>Each attribute can have an optional numeric variable associated with it:</p>
        <ul>
          <li>Attribute variables are numeric values linked to the textual attribute name</li>
          <li>While currently limited in functionality, these variables will enable advanced item formulas in future updates</li>
          <li>These numeric values will provide more granular control over item counts during takeoff</li>
          <li>Consider adding relevant numeric values when creating attributes to prepare for these advanced features</li>
        </ul>
        
        <h3>Unit of Measure (UOM)</h3>
        <p>When creating items, you'll assign a Unit of Measure:</p>
        <ul>
          <li>The UOM will be used in calculating item costs and production values</li>
          <li><strong>Best Practice:</strong> Choose units that align with how you typically purchase the items</li>
          <li>Examples: Linear feet for conduit, each for fittings, square feet for drywall</li>
        </ul>
        
        <p>Consistent and thoughtful UOM selection will make your estimates more accurate and easier to translate into purchasing.</p>
      </div>
      
    `,
  },
  {
    title: 'Unit Costs and Production Values',
    document: `
      <div class="instruction-content">
        <h2>Unit Costs and Production Values</h2>
        
        <h3>UOM-Based Calculations</h3>
        <p>Both cost calculations and production values in Unity Build are directly tied to the Unit of Measure (UOM) you select for each item:</p>
        <ul>
          <li>Material costs are calculated per the specified UOM (per foot, per each, per square foot, etc.) To calculate total cost we use (Item Cost Per UOM / UOM Value) * Total Quantity</li>
          <li>Production values represent the labor hours required to install one unit of the specified UOM. To calculate total cost we use (Hours per Unit / UOM Value) * Total Quantity</li>
          <li>All calculations in the closeout section will use these UOM-based values</li>
        </ul>
        
        <h3>Common UOM Conversion Pitfalls</h3>
        <p>One of the most frequent errors in estimating occurs when transferring source information into Unity Build:</p>
        <ul>
          <li><strong>Source vs. Unity Build UOM Mismatch:</strong> When your source material uses a different UOM than what you've selected in Unity Build</li>
          <li><strong>Missing Conversion Factors:</strong> Failing to convert values when units differ (e.g., source in meters, Unity Build in feet)</li>
          <li><strong>Scale Difference:</strong> Using values for a different scale than what you've established (e.g., values per square meter vs. per square foot)</li>
        </ul>
        
        <h3>Critical Impact on Estimates</h3>
        <p>Incorrect UOM handling can dramatically impact your estimate's accuracy:</p>
        <ul>
          <li>Material costs can be orders of magnitude too high or too low</li>
          <li>Labor estimates may be grossly inaccurate, affecting project scheduling and resource allocation</li>
          <li>Your competitive position could be compromised by either overpricing or underpricing</li>
        </ul>
        
        <h3>Verification Best Practices</h3>
        <p>To ensure accurate cost and production values:</p>
        <ul>
          <li>Always verify the source UOM against your Unity Build UOM</li>
          <li>Calculate and apply any necessary conversion factors before entering values</li>
          <li>Perform "sanity checks" on calculated totals to catch potential UOM errors</li>
          <li>Create a standardized UOM approach across your organization to minimize conversion issues</li>
        </ul>
        
        <p>Taking time to verify UOM consistency will significantly improve the reliability of your estimates and help avoid costly errors in project planning and execution.</p>
      </div>
    `,
  },
  {
    title: "What's in a Labor-Unit?",
    document: `
      <div class="instruction-content" style="background:transparent">
        <h2 style="color:inherit">Building a Labor-Unit from Scratch</h2>
        <p><em>Five-slice method — supervision handled outside the unit.</em></p>
        <p><em>This framework is meant to jog your thinking.  Follow it exactly, bend it, or replace it—but make sure you've asked yourself about every slice of effort before you lock in an hour total.</em></p>
  
        <h3>1&nbsp;•&nbsp;What you're aiming for</h3>
        <p>A <strong>labor-unit</strong> is the number of craft-hours a competent worker (or crew) will spend to install <strong>one</strong> unit of material under normal site conditions.</p>
        <p>You begin by estimating the hands-on <strong>Installation</strong> hours.  The other slices start from simple default percentages of that time and can be nudged up or down to fit the task.</p>
  
        <div class="labor-unit-list" style="display: flex; flex-direction: column; gap: 8px; margin: 16px 0;">
          <div class="list-header" style="display: flex; padding: 12px; background: #2c2c2c; border-radius: 4px; font-weight: 600;">
            <span style="width: 45px; color: inherit;">#</span>
            <span style="width: 190px; color: inherit;">Slice</span>
            <span style="flex-grow: 1; color: inherit;">Typical tasks</span>
            <span style="width: 130px; text-align: right; color: inherit;">Default % of Installation</span>
          </div>
          
          <div class="list-item" style="display: flex; padding: 12px; background: transparent; border-radius: 4px;">
            <span style="width: 45px; color: inherit;">1</span>
            <span style="width: 190px; color: inherit;"><strong>Installation</strong></span>
            <span style="flex-grow: 1; color: inherit;">Cut, fit, weld, fasten, wire, test</span>
            <span style="width: 130px; text-align: right; color: inherit;">— enter hours</span>
          </div>
          
          <div class="list-item" style="display: flex; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 4px;">
            <span style="width: 45px; color: inherit;">2</span>
            <span style="width: 190px; color: inherit;"><strong>Job Layout</strong></span>
            <span style="flex-grow: 1; color: inherit;">Measure, mark, set elevations, coordinate with other trades</span>
            <span style="width: 130px; text-align: right; color: inherit;">15 %</span>
          </div>
          
          <div class="list-item" style="display: flex; padding: 12px; background: transparent; border-radius: 4px;">
            <span style="width: 45px; color: inherit;">3</span>
            <span style="width: 190px; color: inherit;"><strong>Material Handling & Clean-up</strong></span>
            <span style="flex-grow: 1; color: inherit;">Receive, stage, move, unpack, dispose of packaging, keep area tidy</span>
            <span style="width: 130px; text-align: right; color: inherit;">10 %</span>
          </div>
          
          <div class="list-item" style="display: flex; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 4px;">
            <span style="width: 45px; color: inherit;">4</span>
            <span style="width: 190px; color: inherit;"><strong>Non-productive Time</strong></span>
            <span style="flex-grow: 1; color: inherit;">Coffee/stretch breaks, short waits, ladder moves</span>
            <span style="width: 130px; text-align: right; color: inherit;">5 %</span>
          </div>
          
          <div class="list-item" style="display: flex; padding: 12px; background: transparent; border-radius: 4px;">
            <span style="width: 45px; color: inherit;">5</span>
            <span style="width: 190px; color: inherit;"><strong>Tool Handling</strong></span>
            <span style="flex-grow: 1; color: inherit;">Collect tools, set-up, battery swaps, teardown, return to storage</span>
            <span style="width: 130px; text-align: right; color: inherit;">10 %</span>
          </div>
        </div>
  
        <blockquote>
          <p><em>Why no supervision?</em><br>
          Foreman and general-foreman hours are tracked in their own line so you can vary them with crew size without disturbing the base unit.</p>
        </blockquote>
  
        <h3>2&nbsp;•&nbsp;Thinking it through—slice by slice</h3>
        <ol>
          <li><strong>Installation.</strong> Break the work into micro-steps—what must be cut, bent, lifted, fastened, wired, tested? Use field notes, stopwatch studies, published tables, or an informed guess. Write down the resulting hours.</li>
          <li><strong>Job Layout.</strong> How long will measuring, marking, mock-up, or coordination meetings really take? Use the 15 % guide only if it feels right.</li>
          <li><strong>Material Handling &amp; Clean-up.</strong> Picture the journey from truck to install point, plus daily housekeeping. Light? Heavy? Adjust the 10 % guide accordingly.</li>
          <li><strong>Non-productive Time.</strong> Even the best crews pause. If you expect frequent access delays or multiple moves, raise the 5 % placeholder.</li>
          <li><strong>Tool Handling.</strong> Consider specialty gear, battery swaps, or end-of-day teardown. If tools are already staged or you're in a prefab shop, you might cut this slice in half.</li>
        </ol>
        <p>The software will extend or shrink each slice automatically as you tweak the percentages or the installation hours.  What matters is the thought process you just walked through.</p>
  
        <h3>3&nbsp;•&nbsp;Project-specific adjustments (Labor Factors)</h3>
        <p>Your base labor unit assumes typical project conditions. When conditions differ significantly, apply labor factors in the closeout workflow rather than modifying the base unit. This keeps your library of base units "pure" while accounting for project-specific challenges.</p>
        <p>Common conditions that may require labor factors:</p>
        <ul>
          <li>Height work (> 4 m / 12 ft) &rarr; typically +15% to account for additional access time and safety measures</li>
          <li>Congestion / confined space &rarr; affects both installation efficiency and layout time</li>
          <li>Night shift or heavy overtime &rarr; reduced productivity (often 10–15%)</li>
          <li>Extreme weather &rarr; per company guidelines for specific conditions</li>
        </ul>
        <p>Document your labor factors separately from the base unit—this helps track which conditions drove your final estimate and maintains reusability of your base units.</p>
  
        <h3>4&nbsp;•&nbsp;What to leave out</h3>
        <p>Don't fold these into a labor-unit; budget them separately:</p>
        <ul>
          <li>Structural or civil work (coring, trenching, blasting)</li>
          <li>Crane time or hoisting above three floors</li>
          <li>Factory assembly of packaged equipment</li>
          <li>Surface finishing (painting, coating, polishing)</li>
          <li>Startup, commissioning, extended testing, third-party inspections</li>
        </ul>
  
        <h3>5&nbsp;•&nbsp;Where to find starting points</h3>
        <ul>
          <li>Use published benchmarks as sense-checks, not gospel.  Your own field data is king.</li>
        </ul>
  
        <blockquote>
          <p><strong>Need starting points?</strong><br>
          • <em>NECA Manual of Labor Units</em> (electrical)<br>
          • <em>RSMeans Labor Cost Data</em> (general construction)<br>
          • Trade guides—SMACNA (HVAC), MCA (pipe), TCNA (tile) and others—for discipline-specific hints.</p>
        </blockquote>
      </div>
    `,
  },

  {
    title: 'Assemblies',
    document: `
      <div class="instruction-content">
        <h2>Assemblies</h2>
        <p>Assemblies are containers for items that streamline the takeoff process for related components.</p>
        
        <h3>Assembly Basics</h3>
        <ul>
          <li>An assembly can contain one or many items</li>
          <li>There's no strict limit on the number of items per assembly, though performance may be affected by extremely large assemblies</li>
          <li>Testing has shown that assemblies with up to 25 items perform without issues</li>
        </ul>
        
        <h3>Creating an Assembly</h3>
        <ol>
          <li>Set the Assembly Manager to Assembly Mode using either:
            <ul>
              <li>The button at the top right of the component</li>
              <li>The wrench icon on the assembly folder</li>
            </ul>
          </li>
          <li>Select the folder where you want to add the assembly</li>
          <li>Navigate to the item folders and locate the item(s) you want to include</li>
          <li>Click the + button next to an item to add it to the assembly</li>
          <li>Added items will appear in the item formula panel on the right</li>
        </ol>
        
        <h3>Item Formula Panel</h3>
        <p>The viewport on the right side is called the item formula panel, which allows you to:</p>
        <ul>
          <li>View all items added to the assembly</li>
          <li>Change the selected item to any other item within the same attribute group</li>
          <li>Configure how items are counted during takeoff</li>
        </ul>
        
        <h3>Item Counting and Formulas</h3>
        <p>Items within assemblies are counted based on several factors:</p>
        <ul>
          <li>The assembly's Takeoff Type (linear, area, count, etc.)</li>
          <li>The takeoff variable assigned to each item</li>
          <li>The quantity value set for each measure or takeoff variable</li>
        </ul>
        <p>When the assembly is used in a takeoff, the program will count items according to the values set in the formula, providing accurate quantities for your estimate.</p>
      </div>
    `,
  },
  {
    title: 'Takeoff Variables',
    document: `
      <div class="instruction-content">
        <h2>Takeoff Variables</h2>
        <p>Takeoff variables determine how items are counted relative to how you draw them on plans. As you perform a takeoff, you can include or exclude certain elements of an annotation, affecting whether that annotation is included in the count.</p>
        
        <h3>Variable Types</h3>
        <ul>
          <li><strong>Points:</strong> 
            <ul>
              <li>Represent shapes drawn with each click on the screen</li>
              <li>Available for all Assembly Takeoff Types</li>
              <li>Each point can trigger counting of associated items</li>
            </ul>
          </li>
          <li><strong>Segments:</strong> 
            <ul>
              <li>Parts of line and area counts</li>
              <li>Represent unbroken lines between two active points</li>
              <li>Used for measuring discrete sections of a drawing</li>
            </ul>
          </li>
          <li><strong>Length:</strong> 
            <ul>
              <li>The total scaled length of all active lines drawn on the screen</li>
              <li>Used for linear measurements like conduit runs or wall lengths</li>
            </ul>
          </li>
          <li><strong>Area:</strong> 
            <ul>
              <li>Square foot measurement of enclosed spaces</li>
              <li>Used for surface-based materials like flooring or drywall</li>
            </ul>
          </li>
        </ul>
        
        <h3>Variable Formulas</h3>
        <p>These variables drive the number of items counted in your takeoff:</p>
        <ul>
          <li>Each item in an assembly can be associated with a specific takeoff variable</li>
          <li>You can set a ratio between the variable and the item count</li>
        </ul>
        
        <h3>Example</h3>
        <p>If an item's takeoff variable is set to "point" with a formula of "2 qty of item for every 1 point":</p>
        <ul>
          <li>For each point drawn on the screen, the system will count two of these items</li>
          <li>10 points drawn would result in 20 items counted in your estimate</li>
        </ul>
        
        <p>Using appropriate takeoff variables allows for precise quantification based on how you interact with the plans during takeoff.</p>
      </div>
    `,
  },
  {
    title: 'Takeoff Palette',
    document: `
      <div class="instruction-content">
        <h2>Takeoff Palette</h2>
        <p>The Takeoff Palette is where you select assemblies for your project and organize your takeoff elements.</p>
        
        <h3>Beginning a Takeoff</h3>
        <ol>
          <li>Select a document via the toolbar</li>
          <li>Set the document's scale</li>
          <li>Navigate to the Takeoff Palette</li>
          <li>Create a breakout (required before beginning any takeoff)</li>
          <li>Choose an assembly by clicking on its folder</li>
          <li>Verify selection by checking the notification panel in the top left of the screen</li>
          <li>Begin clicking on the document to count or measure items (windows, doors, receptacles, walls, floors, etc.)</li>
        </ol>
        
        <h3>Completing a Takeoff Count</h3>
        <p>To finish your count, you can either:</p>
        <ul>
          <li>Press Enter on your keyboard</li>
          <li>Right-click with an active takeoff to open the Point Editor and select "Complete Run"</li>
        </ul>
        
        <h3>Continuing an Existing Count</h3>
        <p>If you want to add to an existing count, you have two options:</p>
        <ul>
          <li>Navigate to the Audit Trail in the Takeoff Palette and select the previously counted item</li>
          <li>Left-click on an existing point on the document and select "Add to Run"</li>
        </ul>
        
        <h3>Audit Trail</h3>
        <p>The Audit Trail in the Takeoff Palette provides valuable information:</p>
        <ul>
          <li>Shows all items that have been counted</li>
          <li>Displays the quantity of each item counted in your takeoff</li>
          <li>Allows you to select and continue counting existing items</li>
        </ul>
        
        <h3>Breakouts</h3>
        <p>Breakouts are an essential organizational feature:</p>
        <ul>
          <li>Required before beginning any takeoff</li>
          <li>Help organize counts into logical categories</li>
          <li>Useful for creating a schedule of values</li>
          <li>Simplify organization of large bids into manageable parts</li>
        </ul>
      </div>
    `,
  },
  {
    title: 'Point & Color Editor',
    document: `
      <div class="instruction-content">
        <h2>Point & Color Editor</h2>
        <p>The Point Editor and Color Editor are tools that enhance your ability to modify and style takeoff annotations.</p>
        
        <h3>Point Editor</h3>
        <p>The Point Editor allows you to modify takeoff that has been executed to the screen. You access it by right-clicking on a point annotation and then selecting one of its options.</p>
        <p>The Point Editor offers several options:</p>
        <ul>
          <li>Copy/paste points</li>
          <li>Extend existing annotations</li>
          <li>Delete points</li>
          <li>Insert new points</li>
          <li>Other modification options</li>
        </ul>
        
        <h3>Color Editor</h3>
        <p>The Color Editor is activated in the toolbar. Once activated, you can:</p>
        <ul>
          <li>Right-click on the point annotation you want to edit</li>
          <li>The Color Editor will apply a new style to the entire assembly</li>
        </ul>
        <p>This allows you to visually distinguish different types of takeoff on your plans.</p>
      </div>

      <h3>Copy/Paste Points</h3>
      <p>To copy and paste points, follow these steps:</p>
      <ol>
        <li>Select the points you want to copy by pressing the left mouse button and dragging over the desired points</li>
        <li>Right-click on the selected points and choose either:
          <ul>
            <li><strong>Copy Extend</strong> - Allows you to extend your current takeoff</li>
            <li><strong>Copy As New</strong> - Creates a completely new takeoff based on your selection</li>
          </ul>
        </li>
        <li>Move to the location where you want to paste the points</li>
        <li>Right-click and select "Save at Current Position"</li>
      </ol>
      
      <p><strong>Note:</strong> When using "Copy As New":</p>
      <ul>
        <li>You can navigate to a different document before pasting</li>
        <li>The system will use your currently selected breakout when creating the new takeoff</li>
      </ul>
    `,
  },
  {
    title: 'Breakouts',
    document: `
      <div class="instruction-content">
        <h2>Breakouts</h2>
        <p>Breakouts are required and organize your takeoff into logical categories. They create your schedule of values and enable smart multiplier systems to eliminate redundant counting.</p>
        
        <h3>⚠️ Critical: Plan Your Schedule of Values First</h3>
        <p><strong>Know your customer's SoV requirements before creating breakouts.</strong> If you don't align your breakout structure with how the customer needs costs presented, you'll create significant rework later. This system is powerful, but only if your breakouts match your final deliverable structure.</p>

        <h3>Standard vs Typical Breakouts</h3>
        <ul>
          <li><strong>Standard Breakouts:</strong> Multiplier of 1. Use for unique systems (Lighting, Distribution, Ductwork)</li>
          <li><strong>Typical Breakouts:</strong> Custom multipliers. Count once, apply to repetitive areas</li>
        </ul>

        <h4>Example</h4>
                 <div style= padding: 15px; border-radius: 5px; margin: 10px 0;">
          <p><strong>Standard:</strong> "Lighting" → <strong>Typical:</strong> "Lighting - Levels 1-10" (x10)</p>
          <p><em>Count lighting once using the typical breakout, automatically applies to all 10 levels</em></p>
        </div>

        <h3>Key Benefits</h3>
        <ul>
          <li>Reduce repetitive counting by up to 90%</li>
          <li>Perfect for multifamily, hotels, office buildings</li>
          <li>Maintain accurate cost tracking with multipliers</li>
          <li>Flexible, if the customer changes the buildings room counts, you can simply change the multipliers vs redoing the entire takeoff</li>
        </ul>

        <h3>Best Practices</h3>
        <ul>
          <li><strong>Plan first:</strong> Understand customer SoV requirements before starting</li>
          <li><strong>Use descriptive names:</strong> "Typical 2BR Apt" not "Breakout 1"</li>
          <li><strong>Verify multipliers:</strong> Double-check counts for accuracy</li>
        </ul>
      </div>
    `,
  },
  {
    title: 'Closeout',
    document: `
      <div class="instruction-content">
        <h2>Closeout</h2>
        <p>The Closeout section is where you finalize your estimate and prepare it for presentation to clients or stakeholders.</p>
        
        <h3>Bid Extension</h3>
        <p>The bid extension provides a comprehensive view of your takeoff:</p>
        <ul>
          <li>All items from all assemblies are aggregated per row</li>
          <li>Allows you to verify cost and production values for each item</li>
          <li>Displays total quantities from your takeoff</li>
          <li>Enables final adjustments to prices or quantities</li>
        </ul>
        
        <h3>Labor and General Conditions</h3>
        <p>This section allows you to account for additional project costs:</p>
        <ul>
          <li>Labor costs based on your production values</li>
          <li>General conditions that need to be considered for each project</li>
          <li>Project-specific requirements that affect pricing</li>
          <li>Customizable fields to account for unique project circumstances</li>
        </ul>
        
        <h3>Total Pricing</h3>
        <p>The total pricing tab provides final financial calculations:</p>
        <ul>
          <li>Set your overhead percentage</li>
          <li>Configure profit margins</li>
          <li>View calculated totals for the entire project</li>
          <li>Make final adjustments before presenting to clients</li>
        </ul>
        
        <h3>Schedule of Values</h3>
        <p>The schedule of values tab offers organized financial breakdowns:</p>
        <ul>
          <li>Shows how costs have been allocated to your breakouts</li>
          <li>Provides a clear view of costs by category</li>
          <li>Helps identify high-cost areas of the project</li>
          <li>Creates presentation-ready cost breakdowns for clients and stakeholders</li>
        </ul>
        
        <p>The closeout workflow brings together all aspects of your takeoff process, transforming raw counts and measurements into a complete, professional estimate ready for presentation and execution.</p>
      </div>
    `,
  },
  {
    title: 'Chat Bot',
    document: `
      <div class="instruction-content">
        <h2>Chat Bot</h2>
        <p>The chat bot is your AI agent ready to answer questions about your documents, or perform web search for you. The agent can search the web on its own, however if it gets stubborn you can click the world icon at the bottom of the chat panel and we will give it an extra nudge to search the web for info you need.</p>
        
        <h3>AI Limitations</h3>
        <p>AI chat is an emerging technology and it does not behave in a manner we expect from computers. It does not know if what it is saying is factual, therefore you must use caution with taking what it says and treating it as absolute truth. AI as it is today, is best used to iterate ideas, to unlock creativity, and to help you get "unstuck".</p>
        
        <h3>Unity Build's AI Vision</h3>
        <p>Unity Build aims to use AI to continually improve and expand on our ability to help you produce more reliable estimates while acknowledging its limitations.</p>
      </div>
    `,
  },
  {
    title: 'Multi Monitor Support',
    document: `
      <div class="instruction-content">
        <h2>Multi Monitor Support</h2>
        <p>Unity Build supports the use of multiple monitors to enhance your workflow:</p>
        
        <h3>Setting Up Multiple Monitors</h3>
        <ul>
          <li>Log into your account as many times as needed on the same PC</li>
          <li>Each monitor can display different components of the application</li>
          <li>Arrange screens to optimize your workflow (plans on one screen, takeoff palette on another, etc.)</li>
        </ul>
        
        <h3>Syncing Between Monitors</h3>
        <ul>
          <li>To sync progress of inputs produced on another screen, press the refresh button</li>
          <li>The refresh button is located on the far right side of the toolbar</li>
          <li>This ensures all your screens display the most current data</li>
        </ul>
        
        <p>This feature is particularly useful for complex projects where viewing multiple documents or workspaces simultaneously increases productivity.</p>
      </div>
    `,
  },
];
